/**
 * MobileNavDrawer — the mobile + tablet (< xl) navigation drawer.
 * ---------------------------------------------------------------------------
 * #RESP-tablet: the desktop <Sidebar> is now `hidden xl:flex` (it + the RightPanel
 * crushed <main> to ~0px between 768–1279px), so on phones AND tablets navigation
 * lives here: a hamburger in the TopBar opens a left Sheet with the SAME
 * context-aware nav sections. Tapping a link closes it. Shown below `xl`.
 */
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, LayoutDashboard, LayoutGrid, Settings, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { useContextNavSections } from '@/hooks/use-context-nav'
import { ThemeSwitcher } from './ThemeSwitcher'
import type { NavItem } from '@/config/nav-registry'

function DrawerLink({ href, icon: Icon, label, onNavigate }: {
  href: string
  icon: React.ElementType
  label: string
  onNavigate: () => void
}) {
  return (
    <NavLink
      to={href}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]'
            : 'text-foreground/80 hover:bg-accent hover:text-foreground',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  )
}

function DrawerSection({ label, items, onNavigate }: {
  label: string
  items: NavItem[]
  onNavigate: () => void
}) {
  const location = useLocation()
  if (!items.length) return null
  return (
    <div className="space-y-0.5">
      <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {items.map((item) => {
        if (item.children && item.children.length > 0) {
          const activeChild = item.children.some((c) => c.href && location.pathname.startsWith(c.href))
          return (
            <div key={item.title} className="space-y-0.5">
              <div className="flex items-center gap-2 px-3 pt-2 text-[11px] font-semibold text-muted-foreground/80">
                <item.icon className="h-3.5 w-3.5" />
                {item.title}
              </div>
              <div className={cn('ml-4 space-y-0.5 border-l pl-2', activeChild && 'border-primary/40')}>
                {item.children.map((child) =>
                  child.href ? (
                    <DrawerLink key={child.href} href={child.href} icon={child.icon} label={child.title} onNavigate={onNavigate} />
                  ) : null,
                )}
              </div>
            </div>
          )
        }
        return item.href ? (
          <DrawerLink key={item.href} href={item.href} icon={item.icon} label={item.title} onNavigate={onNavigate} />
        ) : null
      })}
    </div>
  )
}

export function MobileNavDrawer() {
  const [open, setOpen] = useState(false)
  const { commonItems, contextItems, footerItems } = useContextNavSections()
  const { i18n } = useTranslation()
  const close = () => setOpen(false)
  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {/* #RESP-R3: the persistent sidebar now renders from `lg` (1024+), so the
            hamburger only shows below `lg`. */}
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 lg:hidden"
          aria-label="Menu de navigation"
          data-testid="mobile-nav-trigger"
        >
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[86vw] max-w-[320px] p-0">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SheetDescription className="sr-only">Menu de navigation principal</SheetDescription>
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-400 shadow">
            <span className="text-[12px] font-black text-white">CP</span>
          </div>
          <span className="text-base font-bold">GovRisk</span>
        </div>
        <ScrollArea className="h-[calc(100vh-3.5rem)]">
          <nav aria-label="Navigation mobile" className="flex flex-col gap-0.5 p-2">
            <DrawerLink href="/dashboard" icon={LayoutDashboard} label="Tableau de bord" onNavigate={close} />
            <DrawerLink href="/hub" icon={LayoutGrid} label="Hub" onNavigate={close} />
            <DrawerSection label="Fonctionnalités" items={contextItems} onNavigate={close} />
            <DrawerSection label="Projet & Gestion" items={commonItems.filter((i) => i.title !== 'Tableau de bord')} onNavigate={close} />
            <DrawerSection label="Outils & Admin" items={footerItems} onNavigate={close} />
            <div className="mt-2 border-t pt-2">
              <DrawerLink href="/settings" icon={Settings} label="Paramètres" onNavigate={close} />
            </div>

            {/* R9: theme picker (10 accents × light/dark/system) + language were
                unreachable on phones — surface them here in the drawer. */}
            <div className="mt-2 flex items-center gap-2 border-t px-3 pt-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Apparence</span>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleLanguage}
                  className="h-8 gap-1 rounded-full px-2 text-xs"
                  aria-label="Changer de langue"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {i18n.language === 'fr' ? 'FR' : 'EN'}
                </Button>
                <ThemeSwitcher />
              </div>
            </div>
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

export default MobileNavDrawer
